import { useParams } from 'react-router';
import { useOrganizationContentQuery } from '@/state/queries/content/use-organization-content.ts';
import Box from '@mui/material/Box';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import { MoreHoriz } from '@mui/icons-material';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';

export default function OrganizationContentList() {
  const { organizationId } = useParams();
  const activeOrganization = useActiveOrganizationStore((state) => state.activeOrganization);

  if (!organizationId || !activeOrganization || organizationId !== activeOrganization.id) {
    return null;
  }

  return <OrganizationContentListForOrganization organizationId={organizationId} />;
}

function OrganizationContentListForOrganization({ organizationId }: { organizationId: string }) {
  const t = useTranslations();
  const notification = useNotifications();
  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useOrganizationContentQuery(organizationId);

  if (error) {
    notification.notifyError(t('content.notification.loadOrganizationContentError'));
    return;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!data?.pages[0].data.length) {
    return (
      <Alert variant="outlined" severity="info" sx={{ margin: 'auto 10%' }}>
        <Typography variant="body1">{t('content.noOrganizationContent')}</Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ margin: 'auto 10%' }}>
      {data?.pages.map((page) =>
        page.data.map((content) => (
          <Accordion key={content.id}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '.MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <Avatar variant="rounded" sx={{ mr: '1rem' }}>
                <PersonIcon />
              </Avatar>
              <Stack>
                <Typography variant="h6">{content.versions[content.versions.length - 1].title}</Typography>
                <Typography variant="body2">
                  {content.versions[content.versions.length - 1].standardDurationMinutes} min.
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">
                {content.versions[0].description || t('content.noContentDescription')}
              </Typography>
            </AccordionDetails>
          </Accordion>
        )),
      )}
      <Stack alignItems="center" sx={{ mt: '1rem' }}>
        <IconButton size="small" onClick={() => fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage}>
          {isFetchingNextPage ? <CircularProgress /> : hasNextPage ? <MoreHoriz /> : null}
        </IconButton>
      </Stack>
    </Box>
  );
}
